// Built-in Import
import * as React from "react";

// External Import
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, Paper, Stack, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";

// Internal Import
import { LoadingEmbraced, MyDatePicker } from "@/components/shared";
import { ApexOptions } from "apexcharts";
import moment from "jalali-moment";
import { humanDate } from "@/utils";
import MyYearPicker from "../datepicker/MuYearPicker";
import useFetch from "@/hook/use-fetch";
import { IResponseForFetchEntity } from "@/api/types";
import { getEndpoint } from "@/api";
import RefreshIcon from "@mui/icons-material/Refresh";

// type IChartTypes = ...
type IChartTypes = any;

interface IAxisChartProps {
  title: string;
  endpoint: string;
  chartOptions?: ApexOptions;
  chartType?: IChartTypes;
  chartColor?: string;
  seriesName?: string;
}


// Define types for API response
interface IProjectItem {
  project_id: string;
  project_title: string;
  created_at: string;
  custom_date?: string;
  estimated_revenue: string;
}

interface IProjectRevenuesResponse {
  status: string;
  message: string;
  data: {
    items: IProjectItem[];
  };
}


const AxisChart: React.FunctionComponent<IAxisChartProps> = ({
  title,
  endpoint,
  chartOptions,
  chartType = "area",
  chartColor = "#E29314",
  seriesName = "",
}) => {

  const { t } = useTranslation(["common"]);
  const [queries, setQueries] = React.useState({});

  const theme = useTheme<Theme>()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const [selectedYear, setSelectedYear] = React.useState<number>(
    moment().locale("fa").jYear() // default to current Jalali year
  );

  // ✅ Your new API call
  const { data, loading } = useFetch<IProjectRevenuesResponse>(
    getEndpoint("userProjectRevenues")
  );

  const handleChangeQuery = (key: string, value: any) => {
    setQueries((crt) => ({ ...crt, [key]: value }));
  };

  const [selectedHalf, setSelectedHalf] = React.useState<"first" | "second">("first");



  // React.useEffect(() => {
  //   if (endpoint) {
  //     // We ignore this since data is local
  //   }
  // }, [endpoint]);


  // ✅ Filter items by selected Jalali year

  // const filteredItems = (data?.data.items || []).filter((item) => {
  //   const gregorianDate = item.created_at.split(" ")[0].trim();
  //   const jYear = humanDate(gregorianDate, "jYYYY")

  // //  const gregorianDate = item.created_at.split(" ")[0].trim();
  // //  const jYear = moment(gregorianDate, "YYYY-MM-DD").locale("fa").jYear().toString();


  //   return jYear === String(selectedYear);
  // });


  // ✅ Filter items by selected Jalali year
  const filteredItems = (data?.data.items || []).filter((item) => {
    // Prefer custom_date if available
    const dateToUse = item.custom_date || item.created_at;
    const gregorianDate = dateToUse.split(" ")[0].trim();
    const jYear = humanDate(gregorianDate, "jYYYY");

    return jYear === String(selectedYear);
  });

  // All 12 Jalali months in order
  const allMonths = [
    "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
    "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"
  ];
  // const allMonths = [
  //   "۱", "۲", "3", "4", "5", "6",
  //   "7", "8", "9", "10", "11", "12"
  // ];

  // ✅ Group by month
  // const rawGrouped: { [month: string]: number } = {};
  // if (filteredItems) {
  //   filteredItems.forEach((item) => {
  //     const date = item.created_at.split(" ")[0].trim();
  //     // const month = moment(date, "YYYY-MM-DD").locale("en").format("jMMMM");
  //     const jalaliMonthIndex = Number(humanDate(date, "jM"));
  //     const month = allMonths[jalaliMonthIndex - 1]; // 0-based index

  //     const revenue = parseFloat(item.estimated_revenue);
  //     rawGrouped[month] = (rawGrouped[month] || 0) + revenue;
  //   });
  // }

  const rawGrouped: { [month: string]: number } = {};
  if (filteredItems) {
    filteredItems.forEach((item) => {
      const dateToUse = item.custom_date || item.created_at;
      const date = dateToUse.split(" ")[0].trim();
      const jalaliMonthIndex = Number(humanDate(date, "jM"));
      const month = allMonths[jalaliMonthIndex - 1]; // 0-based index

      const revenue = parseFloat(item.estimated_revenue);
      rawGrouped[month] = (rawGrouped[month] || 0) + revenue;
    });
  }

  let fullStats = allMonths.map((month) => ({
    date: month,
    aggregate: rawGrouped[month] || 0,
  }));

  if (isMobile) {
    const start = selectedHalf === "first" ? 0 : 6;
    fullStats = fullStats.slice(start, start + 6);
  }

  const statisticsItems2 = fullStats.reverse(); // RTL


  const selectedDays = React.useMemo(() => {
    return statisticsItems2.map((item) => item.date); // already in Jalali month
  }, [statisticsItems2]);

  const selectedValues = React.useMemo(() => {
    return statisticsItems2.map((item) => item.aggregate);
  }, [statisticsItems2]);


  const resetChart = () => {
    const currentYear = moment().locale("fa").jYear();
    setSelectedYear(currentYear);
    setQueries({});
  };

 
  return (
    <Paper
      sx={{
        height: "100%",
        justifyContent: "space-between",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {loading || statisticsItems2 === undefined ? (
        <LoadingEmbraced />
      ) : (
        <>
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={"space-between"}
          // spacing={isMobile ? 2 : 0}
          >
            <Typography variant="bodyMedium">{title}</Typography>
            <Stack direction="row"
              // spacing={isMobile ? 2 : 1.5}
              alignItems="center"
              justifyContent={"space-between"}>
              <Stack
                alignItems="center"

              >
                <MyYearPicker
                  value={moment(`${selectedYear}`, "jYYYY").toDate()}
                  onChange={(date) => {
                    const year = moment(date).locale("fa").jYear();
                    setSelectedYear(year);
                  }}
                />
              </Stack>
              <IconButton onClick={resetChart} size="small">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Stack>


          </Stack>
          <ReactApexChart
            // height={isMobile ? 280 : 360} // 👈 taller bars on desktop
            options={{
              ...chartOptions,
              ...(chartColor && { colors: [chartColor] }),
              chart: {
                ...chartOptions?.chart, type: chartType,
                fontFamily: "inherit", // ✅ inherits global font
              },
              grid: {
                padding: {
                  left: 30,
                  // right: isMobile ? 0 : 50,
                  top: 10,
                  bottom: 10,
                },
              },
              xaxis: {
                categories: selectedDays,
                labels: {
                  // offsetY: isMobile ? 25 : 0,
                  // rotate: isMobile ? -45 : 0,
                  style: {
                    fontSize: isMobile ? "10px" : "12px",
                    fontFamily: "inherit",

                  },


                },
              },
              yaxis: {

                opposite: true,
                labels: {
                  offsetX: isMobile ? -40 : -50,
                  formatter: (val: number) => val.toLocaleString("en-US"),
                  style: {
                    fontSize: isMobile ? "10px" : "12px",
                    fontFamily: "inherit", // ✅ inherits global font
                  },
                },
              },


              plotOptions: {
                bar: {
                  columnWidth: "40%", // Smaller width to add space 
                  distributed: false,
                  borderRadius: 4,
                  dataLabels: {
                    position: "top", // optional: move labels above bars

                  },
                },
              },
              dataLabels: {
                enabled: true,
                formatter: function (val: number) {
                  return val === 0 ? "" : val.toLocaleString("en-US"); // Empty string hides text
                },
                style: {
                  fontFamily: "inherit",
                  fontSize: "12px",
                  // colors: selectedValues.map((val) => (val === 0 ? "#E29314" : "#E29314")), // 🎯 transparent for 0s
                },
                background: {
                  enabled: true,
                  foreColor: "#fff",
                  padding: 4,
                  borderRadius: 5, // ✅ Rounded corners here
                  // backgroundColor: "#E29314",
                  borderWidth: 0,
                },
              },

              tooltip: {
                style: {
                  fontFamily: "inherit", // ✅ inherits global font
                },
              },
            }}
            series={[{ name: seriesName || "سود دریافتی", data: selectedValues }]}
            type={chartType}
            height={isMobile ? 280 : 360} // 👈 taller bars on desktop
          />
          {isMobile && (
            <Stack direction={"row"}
              spacing={isMobile ? 0.5 : 1.5}
              justifyContent={"center"}
            >
              <Stack direction="row" spacing={0.5} alignItems="center"
              >
                <Typography
                  onClick={() => setSelectedHalf("first")}
                  sx={{
                    fontSize: "0.7rem",
                    padding: "2px 6px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    bgcolor: selectedHalf === "first" ? "primary.main" : "transparent",
                    color: selectedHalf === "first" ? "white" : "text.primary",
                    border: "1px solid",
                    borderColor: selectedHalf === "first" ? "primary.main" : "grey.400",
                  }}
                >
                  نیم‌سال اول
                </Typography>

                <Typography
                  onClick={() => setSelectedHalf("second")}
                  sx={{
                    fontSize: "0.7rem",
                    padding: "2px 6px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    bgcolor: selectedHalf === "second" ? "primary.main" : "transparent",
                    color: selectedHalf === "second" ? "white" : "text.primary",
                    border: "1px solid",
                    borderColor: selectedHalf === "second" ? "primary.main" : "grey.400",
                  }}
                >
                  نیم‌سال دوم
                </Typography>
              </Stack>

            </Stack>

          )}
        </>
      )}
    </Paper>
  );
};

export default AxisChart;
